import type { EventDateStoryblok } from "@/component-types-sb";
import { parseBerlinDate, startOfTodayBerlin } from "@/lib/utils/berlinDate";

export type OccurrenceEntry = {
  story: any;
  displayDate: Date;
  originalDate: Date;
  endDate?: Date;
  isRepeating: boolean;
  dateChanged: boolean;
  isCancelled: boolean;
};

/** Get the display date (changed_date if set, otherwise date). */
function getDisplayDate(d: EventDateStoryblok): Date {
  return parseBerlinDate(d.changed_date || d.date);
}

/** Get the original/identity date (always the date field). */
function getOriginalDate(d: EventDateStoryblok): Date {
  return parseBerlinDate(d.date);
}

/** All non-cancelled dates that are still active (today or future), sorted by display date. */
export function getFutureOccurrences(dates: EventDateStoryblok[]): Date[] {
  const cutoff = startOfTodayBerlin();
  return dates
    .filter((d) => !d.is_cancelled)
    .map((d) => getDisplayDate(d))
    .filter((d) => d >= cutoff)
    .sort((a, b) => a.getTime() - b.getTime());
}

/** All dates with full metadata, including cancelled/changed. Sorted by display date.
 *  Deduplicates by displayDate (can happen when a changed_date matches the main date). */
export function getAllOccurrencesWithMeta(dates: EventDateStoryblok[]): {
  displayDate: Date;
  originalDate: Date;
  dateChanged: boolean;
  isCancelled: boolean;
}[] {
  const sorted = dates
    .map((d) => ({
      displayDate: getDisplayDate(d),
      originalDate: getOriginalDate(d),
      dateChanged: !!d.changed_date,
      isCancelled: !!d.is_cancelled,
    }))
    .sort((a, b) => a.displayDate.getTime() - b.displayDate.getTime());

  const seen = new Set<number>();
  return sorted.filter((occ) => {
    const key = occ.displayDate.getTime();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** The next upcoming non-cancelled occurrence, or null if all are past. */
export function getNextOccurrence(dates: EventDateStoryblok[]): Date | null {
  const future = getFutureOccurrences(dates);
  return future.length > 0 ? future[0] : null;
}

/**
 * Select which occurrence to display. If `dateParam` (YYYYMMDD) matches an
 * occurrence's original date, use that; otherwise fall back to the next upcoming.
 */
export function selectOccurrence(
  dates: EventDateStoryblok[],
  dateParam?: string,
): Date {
  if (dateParam && /^\d{8}$/.test(dateParam)) {
    const y = parseInt(dateParam.slice(0, 4), 10);
    const m = parseInt(dateParam.slice(4, 6), 10) - 1;
    const day = parseInt(dateParam.slice(6, 8), 10);
    const match = dates.find((d) => {
      const orig = getOriginalDate(d);
      return (
        orig.getFullYear() === y &&
        orig.getMonth() === m &&
        orig.getDate() === day
      );
    });
    if (match) return getDisplayDate(match);
  }

  const next = getNextOccurrence(dates);
  if (next) return next;

  // Fallback: first date
  return getDisplayDate(dates[0]);
}

/**
 * Find the EventDate entry matching a dateParam (YYYYMMDD) by original date.
 */
export function findOccurrenceByParam(
  dates: EventDateStoryblok[],
  dateParam?: string,
): EventDateStoryblok | undefined {
  if (!dateParam || !/^\d{8}$/.test(dateParam)) return undefined;
  const y = parseInt(dateParam.slice(0, 4), 10);
  const m = parseInt(dateParam.slice(4, 6), 10) - 1;
  const day = parseInt(dateParam.slice(6, 8), 10);
  return dates.find((d) => {
    const orig = getOriginalDate(d);
    return orig.getFullYear() === y && orig.getMonth() === m && orig.getDate() === day;
  });
}

/**
 * Expand a Storyblok story into one entry per active/future occurrence.
 * Skips cancelled occurrences. Returns [] for events with no active dates.
 */
export function expandToOccurrences(story: any): OccurrenceEntry[] {
  const content = story.content;
  const endDate = content.end_date
    ? parseBerlinDate(content.end_date)
    : undefined;

  if (content.repeating_dates?.length) {
    const mainAlreadyInRepeating = content.date && content.repeating_dates.some((d: EventDateStoryblok) => d.date === content.date);
    const allDates: EventDateStoryblok[] = [
      ...(!mainAlreadyInRepeating && content.date ? [{ date: content.date, _uid: "main", component: "event_date" as const }] : []),
      ...content.repeating_dates,
    ];
    const cutoff = startOfTodayBerlin();
    const occurrences = allDates
      .filter((d) => !d.is_cancelled)
      .map((d) => ({
        story,
        displayDate: getDisplayDate(d),
        originalDate: getOriginalDate(d),
        endDate: undefined,
        isRepeating: true,
        dateChanged: !!d.changed_date,
        isCancelled: false,
      }))
      .filter((occ) => occ.displayDate >= cutoff)
      .sort((a, b) => a.displayDate.getTime() - b.displayDate.getTime());

    // Deduplicate by displayDate (can happen when a repeating date is
    // changed to the same date as the main event date)
    const seen = new Set<number>();
    return occurrences.filter((occ) => {
      const key = occ.displayDate.getTime();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  if (!content.date) return [];

  const date = parseBerlinDate(content.date);
  const relevantDate = endDate ?? date;
  if (relevantDate < startOfTodayBerlin()) return [];

  return [{
    story,
    displayDate: date,
    originalDate: date,
    endDate,
    isRepeating: false,
    dateChanged: false,
    isCancelled: false,
  }];
}

/** Format a Date as YYYYMMDD for use in URL params. */
export function formatDateParam(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}${m}${d}`;
}
